// EmbryoTransfer.js — مسار نقل الأجنّة كاملًا: غسل ← ربط تزامن ← نقل (v3.5.1).
//
// لم تكن لهذا المسار واجهة في **أيّ** من الفرعين رغم وجود نقاطه في الخادم منذ زمن — فكانت
// الميزة غير قابلة للاستخدام إلّا بنداء API مباشر.
//
// الترتيب في الصفحة **إجباريّ لا تنظيميّ**: النقل يحتاج `flush_id`، والحاضنة يجب أن تكون
// مرتبطة بالمانحة قبل أن تظهر في قائمة النقل. لذا تُعطَّل المرحلة اللاحقة حتى تكتمل
// السابقة — الواجهة تعكس القيد بدل أن تتركه يظهر خطأً بعد ملء النموذج.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpacityIcon from '@mui/icons-material/Opacity';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import api from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';

const COPY = {
  ar: {
    title: 'نقل الأجنّة',
    subtitle: 'المسار كاملاً: غسل الأجنة ← ربط الحاضنة بالمانحة عند التزامن ← نقل الجنين.',
    refresh: 'تحديث',
    selectDonor: 'اختر الحالة المانحة',
    noDonors: 'لا توجد حالات من نوع «مانحة أجنة». أنشئها من صفحة الإنتاج أولاً.',
    flushes: 'عمليات غسل',
    activePairs: 'حاضنات مرتبطة',
    transfers: 'عمليات نقل',
    availableRecipients: 'حاضنات متاحة',
    flushStep: 'غسل الأجنة',
    flushHint: 'الجنين المستخرج هنا هو ما يُنقل لاحقاً — النقل يحتاج غسلة.',
    addFlush: 'تسجيل غسل',
    noFlushes: 'لا توجد عمليات غسل لهذه المانحة.',
    embryos: 'أجنة',
    transferThis: 'نقل جنين من هذه الغسلة',
    needPair: 'اربط حاضنة أولاً',
    noEmbryo: 'لم يُعثر على جنين — لا نقل.',
    usedInTransfers: 'استُخدمت في',
    transferWord: 'نقل',
    pairStep: 'ربط الحاضنة بالمانحة',
    pairHint: 'الربط يبدأ عند التزامن لا عند النقل — ومنه يُشتق لاحقاً أن أم المولود هي المانحة.',
    addPair: 'ربط حاضنة',
    noCandidates: 'لا حاضنات متاحة',
    noPairs: 'لا توجد حاضنات مرتبطة بهذه المانحة.',
    reserved: 'محجوزة',
    usedTag: 'نُقل',
    releasedTag: 'مفكوك',
    release: 'فك الحجز',
    releasedManually: 'فُك يدوياً',
    transferStep: 'عمليات النقل',
    transferHint: 'يُنفذ من بطاقة الغسلة أعلاه. النقل يُغلق حجز الحاضنة تلقائياً.',
    noTransfers: 'لا توجد عمليات نقل بعد.',
    toRecipient: 'إلى',
    recipient: 'الحاضنة',
    pairNote: 'القائمة تعرض الحاضنات المتاحة فقط — المحجوزة لمانحة أخرى أو المنقول إليها جنين لا تظهر.',
    transferTitle: 'نقل الجنين',
    transferNote: 'الاختيار من الحاضنات المرتبطة بهذه المانحة وحدها. وبعد النقل يُغلق حجز الحاضنة تلقائياً فلا تُستخدم مرتين.',
    needPairFirst: 'لا توجد حاضنة مرتبطة — اربط حاضنة في الخطوة ٢ أولاً.',
    flushDate: 'تاريخ الغسل',
    ovulationDate: 'تاريخ الإباضة',
    embryoFound: 'هل وُجد جنين؟',
    yes: 'نعم',
    no: 'لا',
    embryoCount: 'عدد الأجنة',
    grade: 'درجة الجنين',
    medium: 'وسط الغسل',
    overrideReasonFlush: 'سبب التجاوز (إن كان التوقيت خارج النافذة)',
    overrideReasonTransfer: 'سبب التجاوز (إن كان التزامن غير موصى به)',
    syncStart: 'بدء التزامن',
    targetTransfer: 'موعد النقل المستهدف',
    transferDate: 'تاريخ النقل',
    method: 'طريقة النقل',
    notes: 'ملاحظات',
    save: 'حفظ',
    cancel: 'إلغاء',
    ideal: 'مثالي',
    acceptable: 'مقبول',
    borderline: 'حدي',
    notRecommended: 'غير موصى به',
    flushSaved: 'تم تسجيل الغسل.',
    pairSaved: 'تم ربط الحاضنة بالمانحة.',
    released: 'أُعيدت الحاضنة إلى البركة.',
    transferSaved: 'تم تسجيل النقل، وأُغلق حجز الحاضنة.',
    conflict: 'هذه الحاضنة محجوزة لمانحة أخرى.',
    loadError: 'تعذر تحميل البيانات',
    saveError: 'تعذر الحفظ.',
  },
  en: {
    title: 'Embryo transfer',
    subtitle: 'The full path: flush → pair the recipient to the donor at synchronization → transfer.',
    refresh: 'Refresh',
    selectDonor: 'Select donor case',
    noDonors: 'No donor cases found. Create one from the reproduction page first.',
    flushes: 'Flushes',
    activePairs: 'Paired recipients',
    transfers: 'Transfers',
    availableRecipients: 'Available recipients',
    flushStep: 'Embryo flush',
    flushHint: 'The embryo recovered here is what gets transferred — a transfer needs a flush.',
    addFlush: 'Record flush',
    noFlushes: 'No flushes for this donor.',
    embryos: 'Embryos',
    transferThis: 'Transfer an embryo from this flush',
    needPair: 'Pair a recipient first',
    noEmbryo: 'No embryo recovered — nothing to transfer.',
    usedInTransfers: 'Used in',
    transferWord: 'transfer(s)',
    pairStep: 'Pair recipient to donor',
    pairHint: 'Pairing starts at synchronization, not at transfer — the foal’s dam is derived from it.',
    addPair: 'Pair recipient',
    noCandidates: 'No recipients available',
    noPairs: 'No recipients paired with this donor.',
    reserved: 'Reserved',
    usedTag: 'Transferred',
    releasedTag: 'Released',
    release: 'Release',
    releasedManually: 'Released manually',
    transferStep: 'Transfers',
    transferHint: 'Started from a flush card above. A transfer closes the reservation automatically.',
    noTransfers: 'No transfers yet.',
    toRecipient: 'to',
    recipient: 'Recipient',
    pairNote: 'Only available recipients are listed — one reserved for another donor, or already transferred to, does not appear.',
    transferTitle: 'Transfer embryo',
    transferNote: 'Choose from recipients paired with this donor only. The reservation closes automatically so she cannot be used twice.',
    needPairFirst: 'No paired recipient — pair one in step 2 first.',
    flushDate: 'Flush date',
    ovulationDate: 'Ovulation date',
    embryoFound: 'Embryo found?',
    yes: 'Yes',
    no: 'No',
    embryoCount: 'Number of embryos',
    grade: 'Embryo grade',
    medium: 'Flush medium',
    overrideReasonFlush: 'Override reason (if timing is outside the window)',
    overrideReasonTransfer: 'Override reason (if sync is not recommended)',
    syncStart: 'Sync start',
    targetTransfer: 'Target transfer date',
    transferDate: 'Transfer date',
    method: 'Transfer method',
    notes: 'Notes',
    save: 'Save',
    cancel: 'Cancel',
    ideal: 'Ideal',
    acceptable: 'Acceptable',
    borderline: 'Borderline',
    notRecommended: 'Not recommended',
    flushSaved: 'Flush recorded.',
    pairSaved: 'Recipient paired with the donor.',
    released: 'Recipient returned to the pool.',
    transferSaved: 'Transfer recorded; the reservation is closed.',
    conflict: 'This recipient is reserved for another donor.',
    loadError: 'Could not load data',
    saveError: 'Could not save.',
  },
};

const today = () => new Date().toISOString().slice(0, 10);
const caseName = (row) => (row ? [row.inside_horse_name_ar, row.inside_horse_name].filter(Boolean).join(' | ') || `#${row.case_id}` : '-');
const pairName = (p) => [p.recipient_horse_name_ar, p.recipient_horse_name].filter(Boolean).join(' | ') || `#${p.recipient_case_id}`;

export default function EmbryoTransfer() {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const text = COPY[lang];

  const [cases, setCases] = useState([]);
  const [donorCaseId, setDonorCaseId] = useState('');
  const [flushes, setFlushes] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [flushDialog, setFlushDialog] = useState(false);
  const [flushForm, setFlushForm] = useState({ flush_date: today(), ovulation_date: '', flush_medium: '', embryo_found: '1', number_of_embryos: '1', embryo_grade: '', override_reason: '', notes: '' });
  const [pairDialog, setPairDialog] = useState(false);
  const [pairForm, setPairForm] = useState({ recipient_case_id: '', sync_start_date: today(), target_transfer_date: '', notes: '' });
  const [transferDialog, setTransferDialog] = useState(null);
  const [transferForm, setTransferForm] = useState({ recipient_case_id: '', transfer_date: today(), embryo_grade: '', transfer_method: '', override_reason: '', notes: '' });

  const donorCases = useMemo(() => cases.filter((c) => c.case_type === 'Embryo Transfer Donor'), [cases]);
  const donorCase = useMemo(() => cases.find((c) => String(c.case_id) === donorCaseId) || null, [cases, donorCaseId]);
  const activePairs = useMemo(() => pairs.filter((p) => p.status === 'planned' || p.status === 'active'), [pairs]);

  const stats = useMemo(() => ({
    flushes: flushes.length,
    pairs: activePairs.length,
    transfers: transfers.length,
    available: candidates.length,
  }), [flushes, activePairs, transfers, candidates]);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/reproduction-clinic/cases');
      setCases(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || text.loadError);
    } finally {
      setLoading(false);
    }
  }, [text.loadError]);

  const loadDonorDetail = useCallback(async (id) => {
    if (!id) { setFlushes([]); setPairs([]); setCandidates([]); setTransfers([]); return; }
    try {
      const [flushRes, pairRes, candRes, transferRes] = await Promise.all([
        api.get('/reproduction-clinic/embryo-flushes', { params: { donor_case_id: id } }),
        api.get('/reproduction-clinic/sync-pairs', { params: { donor_case_id: id } }),
        // بلا donor_case_id: بركة الحاضنات المتاحة (غير المحجوزة لأحد) — للربط.
        api.get('/reproduction-clinic/recipient-candidates'),
        api.get('/reproduction-clinic/embryo-transfers', { params: { donor_case_id: id } }),
      ]);
      setFlushes(Array.isArray(flushRes.data) ? flushRes.data : []);
      setPairs(Array.isArray(pairRes.data) ? pairRes.data : []);
      setCandidates(Array.isArray(candRes.data) ? candRes.data : []);
      setTransfers(Array.isArray(transferRes.data) ? transferRes.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || text.loadError);
    }
  }, [text.loadError]);

  useEffect(() => { loadCases(); }, [loadCases]);
  useEffect(() => { loadDonorDetail(donorCaseId); }, [loadDonorDetail, donorCaseId]);

  const run = async (fn, okMessage) => {
    setBusy(true); setError(''); setMessage('');
    try {
      await fn();
      setMessage(okMessage);
      await loadDonorDetail(donorCaseId);
    } catch (e) {
      setError(e?.response?.status === 409 ? (e?.response?.data?.message || text.conflict) : (e?.response?.data?.message || text.saveError));
    } finally {
      setBusy(false);
    }
  };

  const saveFlush = () => run(async () => {
    await api.post('/reproduction-clinic/embryo-flushes', {
      donor_case_id: Number(donorCaseId),
      flush_date: flushForm.flush_date,
      ovulation_date: flushForm.ovulation_date || null,
      flush_medium: flushForm.flush_medium || null,
      embryo_found: flushForm.embryo_found === '1',
      number_of_embryos: Number(flushForm.number_of_embryos || 0),
      embryo_grade: flushForm.embryo_grade || null,
      override_reason: flushForm.override_reason || null,
      notes: flushForm.notes || null,
    });
    setFlushDialog(false);
  }, text.flushSaved);

  const savePair = () => run(async () => {
    await api.post('/reproduction-clinic/sync-pairs', {
      donor_case_id: Number(donorCaseId),
      recipient_case_id: Number(pairForm.recipient_case_id),
      sync_start_date: pairForm.sync_start_date || null,
      target_transfer_date: pairForm.target_transfer_date || null,
      donor_ovulation_date: donorCase?.confirmed_ovulation_date || null,
      recipient_ovulation_date: candidates.find((c) => String(c.case_id) === pairForm.recipient_case_id)?.confirmed_ovulation_date || null,
      notes: pairForm.notes || null,
    });
    setPairDialog(false);
    setPairForm({ recipient_case_id: '', sync_start_date: today(), target_transfer_date: '', notes: '' });
  }, text.pairSaved);

  const releasePair = (pair) => run(async () => {
    await api.post(`/reproduction-clinic/sync-pairs/${pair.pair_id}/release`, { reason: text.releasedManually });
  }, text.released);

  const saveTransfer = () => run(async () => {
    if (!transferDialog) return;
    await api.post('/reproduction-clinic/embryo-transfers', {
      donor_case_id: Number(donorCaseId),
      recipient_case_id: Number(transferForm.recipient_case_id),
      flush_id: transferDialog.flush_id,
      transfer_date: transferForm.transfer_date,
      donor_ovulation_date: donorCase?.confirmed_ovulation_date || transferDialog.ovulation_date || null,
      recipient_ovulation_date: null,
      embryo_grade: transferForm.embryo_grade || transferDialog.embryo_grade || null,
      transfer_method: transferForm.transfer_method || null,
      override_reason: transferForm.override_reason || null,
      notes: transferForm.notes || null,
    });
    setTransferDialog(null);
    setTransferForm({ recipient_case_id: '', transfer_date: today(), embryo_grade: '', transfer_method: '', override_reason: '', notes: '' });
  }, text.transferSaved);

  const syncChip = (status) => {
    if (!status) return null;
    const map = {
      Ideal: { label: text.ideal, bg: COLORS.greenSoft },
      Acceptable: { label: text.acceptable, bg: COLORS.cardSoft },
      // «حدّيّ» تحذير حقيقيّ فلا يصحّ عرضه محايداً.
      Borderline: { label: text.borderline, bg: COLORS.roseSoft },
      'Not Recommended': { label: text.notRecommended, bg: COLORS.roseSoft },
    };
    const item = map[status];
    if (!item) return null;
    return <Chip size="small" label={item.label} sx={{ bgcolor: item.bg, fontWeight: 900 }} />;
  };

  return (
    <Box className="smart-page-container" dir={lang === 'ar' ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={text.title}
        subtitle={text.subtitle}
        actions={<Button onClick={loadCases} startIcon={<RefreshIcon />} sx={{ color: COLORS.gold, fontWeight: 900 }}>{text.refresh}</Button>}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Grid container spacing={2.2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={3}><Metric icon={<OpacityIcon />} label={text.flushes} value={stats.flushes} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<LinkIcon />} label={text.activePairs} value={stats.pairs} color={COLORS.blue} soft={COLORS.blueSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<SwapHorizIcon />} label={text.transfers} value={stats.transfers} color={COLORS.green} soft={COLORS.greenSoft} /></Grid>
        <Grid item xs={12} md={3}><Metric icon={<LinkIcon />} label={text.availableRecipients} value={stats.available} color={COLORS.rose} soft={COLORS.roseSoft} /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
        <TextField select fullWidth label={text.selectDonor} value={donorCaseId} onChange={(e) => setDonorCaseId(e.target.value)} sx={{ maxWidth: 560 }}>
          {donorCases.map((c) => (
            <MenuItem key={c.case_id} value={String(c.case_id)}>{`${caseName(c)} (${c.case_status || ''})`}</MenuItem>
          ))}
        </TextField>
        {!donorCases.length && !loading && <Alert severity="warning" sx={{ mt: 2 }}>{text.noDonors}</Alert>}
      </Paper>

      {donorCaseId && (
        <>
          <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={1.5} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{`١ · ${text.flushStep}`}</Typography>
                <Typography variant="body2" sx={{ color: COLORS.muted }}>{text.flushHint}</Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<OpacityIcon />}
                onClick={() => { setFlushForm((f) => ({ ...f, ovulation_date: (donorCase?.confirmed_ovulation_date || '').slice(0, 10) })); setError(''); setFlushDialog(true); }}
                sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}
              >
                {text.addFlush}
              </Button>
            </Stack>
            <Grid container spacing={1.5}>
              {flushes.length ? flushes.map((f) => (
                <Grid item xs={12} sm={6} md={4} key={f.flush_id}>
                  <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}` }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{String(f.flush_date || '').slice(0, 10) || '-'}</Typography>
                        <Typography variant="caption" sx={{ color: COLORS.muted }}>
                          {`${text.embryos}: ${f.number_of_embryos ?? 0}${f.embryo_grade ? ` · ${f.embryo_grade}` : ''}`}
                        </Typography>
                      </Box>
                      {syncChip(f.flush_timing_status)}
                    </Stack>
                    {Number(f.number_of_embryos) > 0 ? (
                      <Button
                        fullWidth
                        size="small"
                        startIcon={<SwapHorizIcon />}
                        disabled={!activePairs.length}
                        onClick={() => { setTransferForm((v) => ({ ...v, embryo_grade: f.embryo_grade || '' })); setError(''); setTransferDialog(f); }}
                        sx={{ mt: 1, color: COLORS.gold, fontWeight: 900 }}
                      >
                        {activePairs.length ? text.transferThis : text.needPair}
                      </Button>
                    ) : (
                      <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mt: 1 }}>{text.noEmbryo}</Typography>
                    )}
                    {Number(f.transfers_count) > 0 && (
                      <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mt: 0.6 }}>
                        {`${text.usedInTransfers} ${f.transfers_count} ${text.transferWord}`}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              )) : <Grid item xs={12}><Typography sx={{ color: COLORS.muted, textAlign: 'center', py: 3 }}>{text.noFlushes}</Typography></Grid>}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...cardSx, p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={1.5} sx={{ mb: 1.5 }}>
              <Box>
                <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{`٢ · ${text.pairStep}`}</Typography>
                <Typography variant="body2" sx={{ color: COLORS.muted }}>{text.pairHint}</Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                disabled={!candidates.length}
                onClick={() => { setError(''); setPairDialog(true); }}
                sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}
              >
                {candidates.length ? text.addPair : text.noCandidates}
              </Button>
            </Stack>
            <Grid container spacing={1.5}>
              {pairs.length ? pairs.map((p) => (
                <Grid item xs={12} sm={6} md={4} key={p.pair_id}>
                  <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}` }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box>
                        <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{pairName(p)}</Typography>
                        <Typography variant="caption" sx={{ color: COLORS.muted }}>{`#${p.recipient_case_id}`}</Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={p.status === 'used' ? text.usedTag : p.status === 'released' ? text.releasedTag : text.reserved}
                        sx={{ bgcolor: p.status === 'used' ? COLORS.greenSoft : p.status === 'released' ? COLORS.cardSoft : COLORS.blueSoft, fontWeight: 900 }}
                      />
                    </Stack>
                    <Box sx={{ mt: 0.8 }}>{syncChip(p.sync_status)}</Box>
                    {(p.status === 'planned' || p.status === 'active') && (
                      <Button fullWidth size="small" startIcon={<LinkOffIcon />} disabled={busy} onClick={() => releasePair(p)} sx={{ mt: 1, color: COLORS.rose, fontWeight: 900 }}>
                        {text.release}
                      </Button>
                    )}
                    {p.released_reason ? <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mt: 0.6 }}>{p.released_reason}</Typography> : null}
                  </Paper>
                </Grid>
              )) : <Grid item xs={12}><Typography sx={{ color: COLORS.muted, textAlign: 'center', py: 3 }}>{text.noPairs}</Typography></Grid>}
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>{`٣ · ${text.transferStep}`}</Typography>
              <Typography variant="body2" sx={{ color: COLORS.muted }}>{text.transferHint}</Typography>
            </Box>
            <Grid container spacing={1.5}>
              {transfers.length ? transfers.map((tr) => (
                <Grid item xs={12} sm={6} md={4} key={tr.transfer_id}>
                  <Paper elevation={0} sx={{ p: 1.4, border: `1px solid ${COLORS.line}` }}>
                    <Typography sx={{ fontWeight: 900, color: COLORS.text }}>{String(tr.transfer_date || '').slice(0, 10) || '-'}</Typography>
                    <Typography variant="body2" sx={{ color: COLORS.muted, mt: 0.4 }}>
                      {`${text.toRecipient} ${tr.recipient_horse_name_ar || `#${tr.recipient_case_id}`}`}
                    </Typography>
                    <Box sx={{ mt: 0.8 }}>{syncChip(tr.sync_status)}</Box>
                  </Paper>
                </Grid>
              )) : <Grid item xs={12}><Typography sx={{ color: COLORS.muted, textAlign: 'center', py: 3 }}>{text.noTransfers}</Typography></Grid>}
            </Grid>
          </Paper>
        </>
      )}

      <Dialog open={flushDialog} onClose={() => setFlushDialog(false)} fullWidth maxWidth="sm" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.addFlush}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={1.2} sx={{ mt: 0.2 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label={text.flushDate} InputLabelProps={{ shrink: true }} value={flushForm.flush_date} onChange={(e) => setFlushForm((f) => ({ ...f, flush_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label={text.ovulationDate} InputLabelProps={{ shrink: true }} value={flushForm.ovulation_date} onChange={(e) => setFlushForm((f) => ({ ...f, ovulation_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label={text.embryoFound} value={flushForm.embryo_found} onChange={(e) => setFlushForm((f) => ({ ...f, embryo_found: e.target.value, number_of_embryos: e.target.value === '1' ? f.number_of_embryos : '0' }))}>
                <MenuItem value="1">{text.yes}</MenuItem>
                <MenuItem value="0">{text.no}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="number" label={text.embryoCount} value={flushForm.number_of_embryos} onChange={(e) => setFlushForm((f) => ({ ...f, number_of_embryos: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={text.grade} value={flushForm.embryo_grade} onChange={(e) => setFlushForm((f) => ({ ...f, embryo_grade: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={text.medium} value={flushForm.flush_medium} onChange={(e) => setFlushForm((f) => ({ ...f, flush_medium: e.target.value }))} />
            </Grid>
            {/* الخادم يطلب سبباً إن كان التوقيت خارج النافذة — يُعرَض دائماً فلا يُفاجأ المستخدم برفض. */}
            <Grid item xs={12}>
              <TextField fullWidth label={text.overrideReasonFlush} value={flushForm.override_reason} onChange={(e) => setFlushForm((f) => ({ ...f, override_reason: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label={text.notes} value={flushForm.notes} onChange={(e) => setFlushForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlushDialog(false)}>{text.cancel}</Button>
          <Button variant="contained" disabled={busy} onClick={saveFlush} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pairDialog} onClose={() => setPairDialog(false)} fullWidth maxWidth="sm" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.addPair}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 1.5 }}>{text.pairNote}</Alert>
          <Grid container spacing={1.2}>
            <Grid item xs={12}>
              <TextField select fullWidth label={text.recipient} value={pairForm.recipient_case_id} onChange={(e) => setPairForm((f) => ({ ...f, recipient_case_id: e.target.value }))}>
                {candidates.map((c) => (
                  <MenuItem key={c.case_id} value={String(c.case_id)}>
                    {[c.horse_name_ar, c.horse_name].filter(Boolean).join(' | ') || `#${c.case_id}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label={text.syncStart} InputLabelProps={{ shrink: true }} value={pairForm.sync_start_date} onChange={(e) => setPairForm((f) => ({ ...f, sync_start_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label={text.targetTransfer} InputLabelProps={{ shrink: true }} value={pairForm.target_transfer_date} onChange={(e) => setPairForm((f) => ({ ...f, target_transfer_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label={text.notes} value={pairForm.notes} onChange={(e) => setPairForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPairDialog(false)}>{text.cancel}</Button>
          <Button variant="contained" disabled={busy || !pairForm.recipient_case_id} onClick={savePair} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(transferDialog)} onClose={() => setTransferDialog(null)} fullWidth maxWidth="sm" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <DialogTitle sx={{ fontWeight: 900 }}>{text.transferTitle}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 1.5 }}>{text.transferNote}</Alert>
          <Grid container spacing={1.2}>
            <Grid item xs={12}>
              <TextField select fullWidth label={text.recipient} value={transferForm.recipient_case_id} onChange={(e) => setTransferForm((f) => ({ ...f, recipient_case_id: e.target.value }))}>
                {activePairs.map((p) => (
                  <MenuItem key={p.pair_id} value={String(p.recipient_case_id)}>{pairName(p)}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label={text.transferDate} InputLabelProps={{ shrink: true }} value={transferForm.transfer_date} onChange={(e) => setTransferForm((f) => ({ ...f, transfer_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={text.grade} value={transferForm.embryo_grade} onChange={(e) => setTransferForm((f) => ({ ...f, embryo_grade: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={text.method} value={transferForm.transfer_method} onChange={(e) => setTransferForm((f) => ({ ...f, transfer_method: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label={text.overrideReasonTransfer} value={transferForm.override_reason} onChange={(e) => setTransferForm((f) => ({ ...f, override_reason: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label={text.notes} value={transferForm.notes} onChange={(e) => setTransferForm((f) => ({ ...f, notes: e.target.value }))} />
            </Grid>
          </Grid>
          {!activePairs.length && <Alert severity="warning" sx={{ mt: 1.5 }}>{text.needPairFirst}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialog(null)}>{text.cancel}</Button>
          <Button variant="contained" disabled={busy || !transferForm.recipient_case_id} onClick={saveTransfer} sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}>{text.save}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Metric({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, display: 'flex', gap: 1.5, alignItems: 'center', minHeight: 104 }}>
      <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: soft, color, display: 'grid', placeItems: 'center' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" sx={{ color, fontWeight: 900 }}>{label}</Typography>
        <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>{value}</Typography>
      </Box>
    </Paper>
  );
}
